import React from 'react';
import { C, OUTCOME_W, OUTCOME_H, nx, ny } from '../flowCanvas.constants';

// ─── OutcomeNode — solid coloured terminal block ─────────────────────────────
//
// A solid-filled rectangle (no card-style border) used to mark outcome states
// such as "Puzzle Solved" or "Puzzle Failed".
//
// nodeConfig in the seeder:
//   nodeConfig.outcomeType  – "success" | "failure"
//                             success → green (#4ade80 → darker solid)
//                             failure → red (#ef4444)

const SUCCESS_FILL  = '#16a34a';   // darker green, readable on canvas
const FAILURE_FILL  = '#dc2626';   // same red as trash/delete buttons
const SUCCESS_DIM   = 'rgba(74,222,128,0.18)';
const FAILURE_DIM   = 'rgba(239,68,68,0.18)';
const FAILURE_TEXT  = '#fee2e2';
const FAILURE_RX    = /(^|[\s_-])(fail(?:ed|ure)?|lost|out[\s_-]?of[\s_-]?moves)([\s_-]|$)/i;

export default function OutcomeNode({
  step,
  selected,
  onClick,
  centreY,
  overrideX,
  overrideY,
}) {
  const cx = overrideX ?? nx(step);
  const cy = overrideY ?? ny(step, centreY);

  const left = cx - OUTCOME_W / 2;
  const top  = cy - OUTCOME_H / 2;

  const title      = (step.stepTitle || '').trim();
  const type       = step.nodeConfig?.outcomeType;
  const failureCue = `${step.stepId || ''} ${title} ${step.stepDescription || ''}`;
  const isFailure  = type === 'failure' || (!type && FAILURE_RX.test(failureCue));
  const isSuccess  = !isFailure;
  const solidFill  = isSuccess ? SUCCESS_FILL : FAILURE_FILL;
  const glowFill   = isSuccess ? SUCCESS_DIM  : FAILURE_DIM;
  const icon       = isSuccess ? '✓' : '✕';
  const iconColor  = isSuccess ? '#fff' : FAILURE_TEXT;
  const textColor  = isSuccess ? '#fff' : FAILURE_TEXT;

  const words      = title.split(/\s+/);
  const MAX_W      = OUTCOME_W - 56;
  const estimateW  = (t) => t.length * 6.2;

  const wrapLines = (ws, maxW) => {
    const lines = [];
    let cur = '';
    ws.forEach(w => {
      const next = cur ? `${cur} ${w}` : w;
      if (estimateW(next) <= maxW) { cur = next; }
      else { if (cur) lines.push(cur); cur = w; }
    });
    if (cur) lines.push(cur);
    return lines.slice(0, 2);
  };

  const lines   = wrapLines(words, MAX_W);
  const lineH   = 13;
  const textCY  = cy - ((lines.length - 1) * lineH) / 2;

  // left icon area width
  const ICON_AREA = 28;

  return (
    <g
      className="fc-node fc-node--outcome"
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      {/* Drop shadow */}
      <rect
        x={left + 2} y={top + 4}
        width={OUTCOME_W} height={OUTCOME_H}
        rx="6" fill="#000" fillOpacity="0.3"
      />

      {/* Outer glow (visible when selected or always subtle) */}
      {selected && (
        <rect
          x={left - 3} y={top - 3}
          width={OUTCOME_W + 6} height={OUTCOME_H + 6}
          rx="9" fill={glowFill}
        />
      )}

      {/* Main body — solid fill */}
      <rect
        x={left} y={top}
        width={OUTCOME_W} height={OUTCOME_H}
        rx="6"
        fill={solidFill}
        stroke={selected ? C.sel : solidFill}
        strokeWidth={selected ? 2 : 0}
      />

      {/* Left icon strip */}
      <rect
        x={left} y={top}
        width={ICON_AREA} height={OUTCOME_H}
        rx="6" fill="rgba(0,0,0,0.20)"
      />
      <rect
        x={left + ICON_AREA - 2} y={top}
        width={2} height={OUTCOME_H}
        fill="rgba(0,0,0,0.15)"
      />

      {/* Icon */}
      <text
        x={left + ICON_AREA / 2}
        y={cy + 1}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="14"
        fontWeight="700"
        fill={iconColor}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >{icon}</text>

      {/* Title text */}
      {lines.map((line, i) => (
        <text
          key={i}
          x={left + ICON_AREA + 8}
          y={textCY + i * lineH}
          dominantBaseline="middle"
          fontSize="10.5"
          fontWeight="700"
          fill={textColor}
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >{line}</text>
      ))}
    </g>
  );
}
